/**
 * The band's backdrop as a layer stack (2026-10-13).
 *
 * Blender renders `band-<mode>-{back,mid,front,glow}.webp` for delve, arena
 * and waves into assets/ui/lobby/layers/. This wires `#hub-band-art` to them
 * through the same `buildLayerStack` the stations use, keeping the current
 * picture (the floor's preview, or the mode's tile) as the background and the
 * fallback: with no layers the band looks exactly as it did.
 *
 * A change of mode or floor crossfades: the outgoing art is cloned into a
 * ghost that fades away over the incoming one, instead of a hard swap.
 */

import { buildLayerStack } from './hub-depth';

const MODE_LAYER_ID: Readonly<Record<string, string>> = {
  DUNGEON: 'band-delve',
  ARENA: 'band-arena',
  WAVES: 'band-waves',
};

/** Wire game-mode id -> layer file prefix, or null for a mode with no art of its own. */
export function bandLayerId(modeId: string): string | null {
  return MODE_LAYER_ID[modeId] ?? null;
}

export interface BandArt {
  /** Show `fallbackUrl` as the picture and the mode's layers (when they exist) over it. */
  update(modeId: string, fallbackUrl: string): void;
}

const GHOST_MS = 520;

export function createBandArt(el: HTMLElement): BandArt {
  const stacks = new Map<string, HTMLElement>();
  let shownKey = '';
  let current: HTMLElement | null = null;

  const reduced = (): boolean =>
    typeof window.matchMedia !== 'function' || window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function crossfadeGhost(): void {
    const parent = el.parentElement;
    if (!parent || reduced()) return;
    const ghost = el.cloneNode(true) as HTMLElement;
    ghost.removeAttribute('id');
    ghost.className = 'band-art band-art-ghost';
    // Freeze the drift where it was, so the ghost does not snap to unscaled.
    ghost.style.transform = getComputedStyle(el).transform;
    parent.insertBefore(ghost, el);
    window.setTimeout(() => ghost.remove(), GHOST_MS + 80);
  }

  return {
    update(modeId, fallbackUrl): void {
      const layerId = bandLayerId(modeId);
      const key = `${modeId}|${fallbackUrl}`;
      if (key === shownKey) return;
      if (shownKey) crossfadeGhost();
      shownKey = key;
      el.style.backgroundImage = `url('${fallbackUrl}')`;

      let next: HTMLElement | null = null;
      if (layerId) {
        next = stacks.get(layerId) ?? null;
        if (!next) {
          const stack = buildLayerStack(layerId, {});
          stack.classList.add('band-stack');
          stack.addEventListener('depthstate', () => { if (current === stack) el.dataset.layered = stack.dataset.state === 'layered' ? '1' : ''; });
          stacks.set(layerId, stack);
          next = stack;
        }
      }
      if (next !== current) {
        current?.remove();
        current = next;
        if (next) el.appendChild(next);
      }
      el.dataset.layered = next && next.dataset.state === 'layered' ? '1' : '';
    },
  };
}
